import {
  BUFF_ATTACK_ABILITY_ID,
  EXPLODING_ABILITY_ID,
  EXPLOSIVE_ABILITY_ID,
  GRANDMASTER_ABILITY_ID,
  GREATER_HEAL_ABILITY_ID,
  HEAL_ABILITY_ID,
  MARK_ABILITY_ID,
  MASTER_BUFF_ABILITY_ID,
  RANDOM_EVOLUTION_ABILITY_ID,
  SPELLMAKER_ABILITY_ID,
  SPELLMASTER_ABILITY_ID,
  SPELLBURST_ABILITY_ID,
  SPELLBLAST_ABILITY_ID,
  SWARMNEST_ABILITY_ID,
  SWARMLORD_ABILITY_ID,
  SECOND_CHANCE_ABILITY_ID,
  SHARED_PAIN_ABILITY_ID,
  WARBAND_ABILITY_ID,
  WARTRIBE_ABILITY_ID,
} from './AbilityConstants';
import { GameConfig } from '../../config/gameConfig';
import {
  GRAVEDIGGER_ABILITY_ID,
  NECROMANCY_ABILITY_ID,
} from './AbilityConstants';
import {
  RUNE_DEAD_ID,
  RUNE_NECROMANCER_ID,
  RUNE_SWARM_ID,
  RUNE_TANK_ID,
  RUNE_WARRIOR_ID,
} from './RuneDefinitions';

/**
 * Represents a single upgrade branch option available from a given state.
 */
export interface UpgradeBranch {
  /** Unique identifier for this upgrade branch (e.g., "hp", "atk", "ability"). */
  id: string;

  /** Human-readable name of the upgrade. */
  name: string;

  /** HP change applied when this upgrade is chosen. Can be negative. */
  hpChange: number;

  /** Attack change applied when this upgrade is chosen. Can be negative. */
  attackChange: number;

  /** Ability IDs added by this upgrade (optional). For "ability" branches,
   * this is populated server-side with the randomly chosen ability ID. */
  abilitiesAdded?: string[];

  /** Ability IDs removed by this upgrade (optional). */
  abilitiesRemoved?: string[];

  /**
   * 'transform' branches ONLY: the class the minion is transformed INTO.
   * A transform branch does not mutate the minion — it replaces it with a
   * fresh level-1 minion of this class (the original is removed; see
   * PlayerState.transformMinion). Stat/ability fields are zero/empty for
   * transform branches. Must match the shared spec (TransformDefinitions) —
   * the server re-validates class + exact level on the request.
   */
  transformTargetClass?: string;

  /** The upgradePathState the minion transitions to after applying this upgrade. */
  nextPathState: string;
}

/**
 * Branch ID of the Rune of High Stakes upgrade option — the single option
 * that REPLACES the +5 HP ('hp') and +5 ATK ('atk') options while the rune
 * is active: +HIGH_STAKES_STAT_BONUS HP AND ATK, with a level-scaled chance
 * to DESTROY the minion instead (RuneDefinitions.highStakesDestroyChance).
 * Shared by the server option builder, the server validation, the bot
 * branch builder and the client render fallback (one string, no drift).
 */
export const HIGH_STAKES_BRANCH_ID = 'high_stakes';

/**
 * Ability definition summary used for upgrade eligibility checks.
 * Lightweight form of AbilityDefinition that avoids importing the full registry.
 */
export interface AbilityDefinitionSummary {
  /** Stable unique identifier for this ability. */
  id: string;
  /** Human-readable name of the ability. */
  name: string;
  /** Short description of what the ability does. */
  description: string;
  /** Type classifications (e.g., ["warrior", "healer"]). */
  types: string[];
  /** Minimum minion level at which this ability can be chosen on upgrade. 0 = any level. */
  availableAtLevel: number;
}

/**
 * Returns the ability IDs an upgrade must REMOVE when it grants the given
 * ability — the abilities the new ability REPLACES. Greater Heal replaces
 * Heal (a minion can only learn Greater Heal if it already has Heal, and
 * never holds both); Exploding replaces Explosive (a minion can only learn
 * Exploding if it already has Explosive, and never holds both); Master Buff
 * replaces BOTH Buff Vitality and Buff Attack (it can only be learned with
 * both, and never holds either alongside it); WarTribe replaces Warband (a
 * minion can only learn WarTribe if it already has Warband, and never holds
 * both). Empty for every other ability.
 *
 * Used by the upgrade-branch builders (human request handler and bot branch
 * builder) so the removal always travels with the addition.
 *
 * @param abilityId The ability ID being granted by the upgrade.
 * @returns Array of ability IDs to remove (empty when nothing is replaced).
 */
export function abilitiesReplacedByUpgrade(abilityId: string): string[] {
  if (abilityId === GREATER_HEAL_ABILITY_ID) return [HEAL_ABILITY_ID];
  if (abilityId === EXPLODING_ABILITY_ID) return [EXPLOSIVE_ABILITY_ID];
  if (abilityId === MASTER_BUFF_ABILITY_ID) {
    return ['buff_vitality', BUFF_ATTACK_ABILITY_ID];
  }
  if (abilityId === WARTRIBE_ABILITY_ID) return [WARBAND_ABILITY_ID];
  if (abilityId === SPELLMASTER_ABILITY_ID) return [SPELLMAKER_ABILITY_ID];
  if (abilityId === SPELLBLAST_ABILITY_ID) return [SPELLBURST_ABILITY_ID];
  return [];
}

/**
 * Optional board context for upgrade eligibility. Only the Second Chance
 * rule needs it (it requires a friendly Grandmaster minion ON THE
 * BATTLEFIELD); call sites without player context simply omit it, which
 * makes Second Chance ineligible everywhere except where the caller
 * supplies the real count.
 */
export interface UpgradeEligibilityContext {
  /**
   * The number of friendly Grandmaster minions on the upgrader's
   * battlefield (PlayerState.countGrandmastersOnBattlefield). Undefined =
   * 0 (no Grandmaster — Second Chance is not offered).
   */
  grandmasterCount?: number;
}

/**
 * Full upgrade-eligibility check for ONE ability on ONE minion. An ability
 * is eligible if:
 * - Its types array includes the minion's minionType
 * - The minion does not already have this ability
 * - Its availableAtLevel is <= the minion's current level
 * - Replacement rules hold:
 *   - Greater Heal requires the minion to ALREADY have Heal (the upgrade
 *     then removes Heal — see abilitiesReplacedByUpgrade), and
 *   - Heal is NEVER offered to a minion that already has Greater Heal
 *     (otherwise both would coexist and both OnAttack heals would fire), and
 *   - Exploding requires the minion to ALREADY have Explosive (the upgrade
 *     then removes Explosive — see abilitiesReplacedByUpgrade), and
 *   - Explosive is NEVER offered to a minion that already has Exploding
 *     (the replacement is one-way: a minion never holds both explosion
 *     forms), and
 *   - Master Buff requires the minion to ALREADY have BOTH Buff Vitality
 *     AND Buff Attack (the upgrade then removes both — see
 *     abilitiesReplacedByUpgrade), and
 *   - Buff Vitality / Buff Attack are NEVER offered to a minion that
 *     already has Master Buff (it is their superset — a minion never holds
 *     it together with either of the two it replaces), and
 *   - WarTribe requires the minion to ALREADY have Warband (the upgrade
 *     then removes Warband — see abilitiesReplacedByUpgrade), and
 *   - Warband is NEVER offered to a minion that already has WarTribe
 *     (a minion never holds both Warband forms), and
 *   - Shared Pain requires the minion to ALREADY have Mark (one-way gate —
 *     unlike the replacement abilities above, Mark is NOT removed, so the
 *     holder keeps marking while it shares pain), and
 *   - SwarmNest requires the minion to ALREADY have SwarmLord (one-way
 *     gate — SwarmLord is NOT removed, so the holder keeps swarming while
 *     its Swarmers spawn without Fragile), and
 *   - Second Chance can only be learned by leveling PAST the base maximum
 *     level, and that extension only exists while a friendly GRANDMASTER
 *     minion is on the battlefield (the Grandmaster level-extension
 *     effect): the minion must be AT or above the base max level (level
 *     GameConfig.maxUpgrades + 1 — its level requirement), a friendly
 *     Grandmaster must be on the battlefield, and the extended upgrade
 *     must still be available (the minion may not already be at the
 *     extended cap — an upgrade that cannot be performed cannot grant the
 *     ability).
 *
 * Shared by every eligibility site (server human upgrade path, server
 * bot branch builder, frontend local fallback) so they always agree.
 *
 * @param def The ability definition to check.
 * @param minionType The minion's type classification (e.g., "warrior", "mage").
 * @param currentAbilities Array of ability IDs the minion already has.
 * @param minionLevel The minion's current level (1 + upgradeCount).
 * @param context Optional board context (grandmasterCount — see
 *   UpgradeEligibilityContext). Omitted = 0 Grandmasters.
 * @returns true when the ability is eligible for this minion.
 */
/**
 * KEYWORD DEPENDENCIES (task 211) — the keywords that can only be learned
 * when the minion ALREADY HAS the listed keyword(s): the "requires" gates
 * in isAbilityEligibleForUpgrade (Greater Heal → Heal, Exploding →
 * Explosive, Master Buff → Buff Vitality + Buff Attack, WarTribe → Warband,
 * SpellMaster → Spellmaker, SpellBlast → Spellburst, Shared Pain → Mark,
 * SwarmNest → SwarmLord; Second Chance → Grandmaster — a battlefield
 * CONTEXT gate, a friendly Grandmaster in play; the grid arrow points at
 * the Grandmaster chip in the same column). Single source for the Class
 * Progression grid's dependency arrows (a supersede pair lists only the
 * "requires" direction — Heal is not listed for Greater Heal).
 */
export const KEYWORD_DEPENDENCIES: Readonly<Record<string, readonly string[]>> = {
  [GREATER_HEAL_ABILITY_ID]: [HEAL_ABILITY_ID],
  [EXPLODING_ABILITY_ID]: [EXPLOSIVE_ABILITY_ID],
  [MASTER_BUFF_ABILITY_ID]: ['buff_vitality', BUFF_ATTACK_ABILITY_ID],
  [WARTRIBE_ABILITY_ID]: [WARBAND_ABILITY_ID],
  [SPELLMASTER_ABILITY_ID]: [SPELLMAKER_ABILITY_ID],
  [SPELLBLAST_ABILITY_ID]: [SPELLBURST_ABILITY_ID],
  [SHARED_PAIN_ABILITY_ID]: [MARK_ABILITY_ID],
  [SWARMNEST_ABILITY_ID]: [SWARMLORD_ABILITY_ID],
  [SECOND_CHANCE_ABILITY_ID]: [GRANDMASTER_ABILITY_ID],
};

/**
 * GRANDMASTER-CONTEXT keywords (task 212) — availability depends on a
 * friendly GRANDMASTER in play (the over-max level-extension effect).
 *
 * GRID MAPPING (task 216): the grid ROW INDEX IS the in-game LEVEL. The
 * BOTTOM row (index 0) is the SPAWN row — the class cards only, NO label
 * ("level 0" — a fresh minion) — and it sits directly below the "Level
 * 1" row (index 1); every row above is labelled "Level L" = index L.
 *
 * What a row shows: what a minion can HAVE at that level.
 *  - The SPAWN keywords — the keywords that come WITH the class card
 *    (def.baseAbilities: Shield / Buff Vitality / Heal / Runekeeper /
 *    Spellmaker / Improved / Fragile) — sit on the LEVEL 1 row (a fresh
 *    minion has them at level 1).
 *  - A LEARNED keyword with availableAtLevel K is granted by the
 *    K → K+1 upgrade (K 0/1 → the first upgrade), so it is first had at
 *    level K+1 → the "Level K+1" row (index K+1; levels 0/1 merge into
 *    the Level 2 row). Examples: Gravedigger (3→4) → Level 4; SwarmLord /
 *    Grandmaster (5→6) → Level 6; SECOND CHANCE (6→7) → the Level 7 row
 *    (you can't have it at level 6).
 *  - The CLASS TRANSFORM is learned on the 3 → 4 upgrade → the LEVEL 4
 *    row (index spec.level + 1).
 *
 * The base cap is 5 upgrades (level 6), so LEVEL 7 is the first level
 * that requires a friendly GRANDMASTER — the Level 7 row is marked
 * "Grandmaster" and the Level 8 row "2 Grandmasters". Grandmaster-
 * context keywords land on those rows by construction (K 6 → Level 7,
 * K 7 → Level 8) and never draw the Grandmaster dependency arrow — the
 * row carries the requirement.
 */
export const GRANDMASTER_CONTEXT_ABILITY_IDS: readonly string[] = [SECOND_CHANCE_ABILITY_ID];

/** The IN-GAME LEVELS of the Grandmaster-context marker rows (task 216):
 *  Level 7 = the first over-max level (requires 1 Grandmaster), Level 8
 *  requires 2. The row INDEX equals the level (the card row is index 0,
 *  unlabeled). */
export const GRANDMASTER_ROW = 7;
export const DOUBLE_GRANDMASTER_ROW = 8;

/**
 * KEYWORD CLASS UNLOCKS (task 212) — the keywords that bring a class's
 * units to the board: SwarmLord spawns Swarmers (the holder's death burst);
 * Gravedigger adds the Undead CARD to the draw pool; Necromancy replaces a
 * dead neighboring minion with a temporary Undead combat copy. Single
 * source for the grid's purple unlock arrows (keyword → the class's
 * level-0 column) + the purple keyword mark.
 */
export const KEYWORD_CLASS_UNLOCKS: Readonly<Record<string, readonly string[]>> = {
  [SWARMLORD_ABILITY_ID]: ['swarmer'],
  [GRAVEDIGGER_ABILITY_ID]: ['undead'],
  [NECROMANCY_ABILITY_ID]: ['undead'],
};

/**
 * RUNE UNIT EFFECTS (task 212) — the runes that unlock a unit class or
 * directly affect it: Rune of the Necromancer (the Undead joins the draw
 * pile) + Rune of the Dead (a dead friendly is replaced by an Undead in
 * combat) → Undead; Rune of the Swarm (Swarmers spawn with Improved) →
 * Swarmer; Rune of the Warrior (+10 ATK) → Warrior; Rune of the Tank
 * (+10 HP) → Tank. Single source for the grid's right-side rune column
 * (stacked runes: icon + name + price, click → description) and the
 * purple arrows from a rune to the unit it unlocks/affects.
 */
export const RUNE_UNIT_EFFECTS: Readonly<Record<string, readonly string[]>> = {
  [RUNE_NECROMANCER_ID]: ['undead'],
  [RUNE_DEAD_ID]: ['undead'],
  [RUNE_SWARM_ID]: ['swarmer'],
  [RUNE_WARRIOR_ID]: ['warrior'],
  [RUNE_TANK_ID]: ['tank'],
};

export function isAbilityEligibleForUpgrade(
  def: AbilityDefinitionSummary,
  minionType: string,
  currentAbilities: string[],
  minionLevel: number,
  context?: UpgradeEligibilityContext
): boolean {
  if (!def.types.includes(minionType)) return false;
  if (currentAbilities.includes(def.id)) return false;
  if (def.availableAtLevel > minionLevel) return false;
  // Greater Heal may only be learned by a minion that already has Heal.
  if (def.id === GREATER_HEAL_ABILITY_ID && !currentAbilities.includes(HEAL_ABILITY_ID)) {
    return false;
  }
  // Heal is superseded by Greater Heal: once replaced, it cannot be
  // re-learned (a minion never holds both heal forms).
  if (def.id === HEAL_ABILITY_ID && currentAbilities.includes(GREATER_HEAL_ABILITY_ID)) {
    return false;
  }
  // Exploding may only be learned by a minion that already has Explosive.
  if (def.id === EXPLODING_ABILITY_ID && !currentAbilities.includes(EXPLOSIVE_ABILITY_ID)) {
    return false;
  }
  // Explosive is superseded by Exploding: once replaced, it cannot be
  // re-learned (a minion never holds both explosion forms).
  if (def.id === EXPLOSIVE_ABILITY_ID && currentAbilities.includes(EXPLODING_ABILITY_ID)) {
    return false;
  }
  // Master Buff may only be learned by a minion that already has BOTH
  // Buff Vitality AND Buff Attack (it is the superset of both).
  if (
    def.id === MASTER_BUFF_ABILITY_ID &&
    (!currentAbilities.includes('buff_vitality') || !currentAbilities.includes(BUFF_ATTACK_ABILITY_ID))
  ) {
    return false;
  }
  // Buff Vitality / Buff Attack are superseded by Master Buff: once
  // replaced, they cannot be re-learned (a minion never holds Master Buff
  // together with either of the two it replaces).
  if (
    (def.id === 'buff_vitality' || def.id === BUFF_ATTACK_ABILITY_ID) &&
    currentAbilities.includes(MASTER_BUFF_ABILITY_ID)
  ) {
    return false;
  }
  // WarTribe may only be learned by a minion that already has Warband.
  if (def.id === WARTRIBE_ABILITY_ID && !currentAbilities.includes(WARBAND_ABILITY_ID)) {
    return false;
  }
  // Warband is superseded by WarTribe: once replaced, it cannot be
  // re-learned (a minion never holds both Warband forms).
  if (def.id === WARBAND_ABILITY_ID && currentAbilities.includes(WARTRIBE_ABILITY_ID)) {
    return false;
  }
  // SpellMaster may only be learned by a minion that already has Spellmaker
  // (the upgrade then REMOVES Spellmaker — see abilitiesReplacedByUpgrade;
  // SpellMaster is the power-2 superset of Spellmaker).
  if (def.id === SPELLMASTER_ABILITY_ID && !currentAbilities.includes(SPELLMAKER_ABILITY_ID)) {
    return false;
  }
  // Spellmaker is superseded by SpellMaster: once replaced, it cannot be
  // re-learned (a minion never holds both spellmaker forms).
  if (def.id === SPELLMAKER_ABILITY_ID && currentAbilities.includes(SPELLMASTER_ABILITY_ID)) {
    return false;
  }
  // SpellBlast may only be learned by a minion that ALREADY has Spellburst
  // (it is the any-level superset — the upgrade REMOVES Spellburst, see
  // abilitiesReplacedByUpgrade).
  if (def.id === SPELLBLAST_ABILITY_ID && !currentAbilities.includes(SPELLBURST_ABILITY_ID)) {
    return false;
  }
  // Spellburst is superseded by SpellBlast: once replaced, it cannot be
  // re-learned (a minion never holds both spell-grant forms).
  if (def.id === SPELLBURST_ABILITY_ID && currentAbilities.includes(SPELLBLAST_ABILITY_ID)) {
    return false;
  }
  // Shared Pain may only be learned by a minion that ALREADY has Mark.
  // One-way gate: the upgrade does NOT remove Mark (the holder keeps
  // marking while it shares — see SHARED_PAIN_ABILITY_ID), so there is no
  // two-direction rule (a minion that has Shared Pain necessarily has Mark,
  // which it can never be re-offered anyway — it is already owned).
  if (def.id === SHARED_PAIN_ABILITY_ID && !currentAbilities.includes(MARK_ABILITY_ID)) {
    return false;
  }
  // SwarmNest may only be learned by a minion that ALREADY has SwarmLord.
  // One-way gate (the Shared Pain / Mark pattern): the upgrade does NOT
  // remove SwarmLord (the holder keeps swarming while its Swarmers lose
  // Fragile — see SWARMNEST_ABILITY_ID), so there is no two-direction rule.
  if (def.id === SWARMNEST_ABILITY_ID && !currentAbilities.includes(SWARMLORD_ABILITY_ID)) {
    return false;
  }
  // Second Chance: only learnable by leveling PAST the base maximum level,
  // which only exists while a friendly Grandmaster minion is on the
  // battlefield (the Grandmaster level-extension effect — see
  // PlayerState.getMaxUpgrades). The three conditions:
  //  - a friendly Grandmaster must be on the battlefield (it is what
  //    creates the over-max upgrade in the first place);
  //  - the minion must be AT or above the base max level (level
  //    GameConfig.maxUpgrades + 1) — the upgrade being performed is the
  //    over-max one (its level requirement of 6 with the default config);
  //  - the extended upgrade must still be available: the minion may not
  //    already be at the extended cap (base max + one per Grandmaster),
  //    because an upgrade that cannot be performed cannot grant the
  //    ability.
  if (def.id === SECOND_CHANCE_ABILITY_ID) {
    const grandmasterCount = context?.grandmasterCount ?? 0;
    if (grandmasterCount < 1) return false;
    const baseMaxLevel = GameConfig.maxUpgrades + 1;
    if (minionLevel < baseMaxLevel) return false;
    if (minionLevel > GameConfig.maxUpgrades + grandmasterCount) return false;
    return true;
  }
  return true;
}

/**
 * Returns abilities that are eligible for upgrade on a minion.
 * An ability is eligible if:
 * - Its types array includes the minion's minionType
 * - The minion does not already have this ability
 * - Its availableAtLevel is <= the minion's current level
 * - Replacement rules hold (see isAbilityEligibleForUpgrade)
 *
 * RANDOM EVOLUTION FALLBACK (Runekeeper, level requirement 1): when — and
 * only when — NO other ability is eligible, the Random Evolution ability
 * itself is offered as the sole entry (it is the last-resort keyword
 * option, never shown alongside a normal choice). Learning it never adds
 * 'random_evolution' to the minion — the upgrade resolves to a random
 * keyword from randomEvolutionPool instead — so the option can be learned
 * multiple times. The grant must be possible (a non-empty pool) for the
 * option to be offered at all.
 *
 * @param minionType The minion's type classification (e.g., "warrior", "mage").
 * @param currentAbilities Array of ability IDs the minion already has.
 * @param minionLevel The minion's current level (1 + upgradeCount).
 * @param allAbilityDefinitions All available ability definitions in the game.
 * @param context Optional board context (grandmasterCount — see
 *   UpgradeEligibilityContext). Omitted = 0 Grandmasters.
 * @returns Array of eligible ability definitions (may be empty; a lone
 *   Random Evolution entry when it is the last-resort offer).
 */
export function getEligibleAbilityUpgrades(
  minionType: string,
  currentAbilities: string[],
  minionLevel: number,
  allAbilityDefinitions: AbilityDefinitionSummary[],
  context?: UpgradeEligibilityContext
): AbilityDefinitionSummary[] {
  // Random Evolution is handled by the fallback below — it is never part
  // of the normal filter (a normal entry would show it alongside other
  // choices, which the offer rule forbids).
  const normal = allAbilityDefinitions.filter(
    (def) =>
      def.id !== RANDOM_EVOLUTION_ABILITY_ID &&
      isAbilityEligibleForUpgrade(def, minionType, currentAbilities, minionLevel, context)
  );
  if (normal.length > 0) return normal;
  // The last-resort offer: Random Evolution stands in ONLY when there is
  // nothing else to offer (and the grant pool is not empty — see the
  // ability's doc in AbilityConstants).
  const randomEvolutionDef = allAbilityDefinitions.find(
    (def) => def.id === RANDOM_EVOLUTION_ABILITY_ID
  );
  if (
    randomEvolutionDef &&
    isAbilityEligibleForUpgrade(randomEvolutionDef, minionType, currentAbilities, minionLevel, context) &&
    randomEvolutionPool(currentAbilities, minionLevel, allAbilityDefinitions).length > 0
  ) {
    return [randomEvolutionDef];
  }
  return normal;
}

/**
 * Minimal RNG shape the Random Evolution grant resolver needs (the
 * authoritative RandomNumberService satisfies it — nextInt's max is
 * EXCLUSIVE, matching every server-side pick convention).
 */
export interface EvolutionRng {
  nextInt(min: number, max: number): number;
}

/**
 * The pool of keywords a Random Evolution grant can roll from: EVERY
 * ability defined in the game (ANY class — the grant is class-agnostic by
 * design), level-gated (availableAtLevel <= the minion's level — the grant
 * is always "in the pool of available abilities"), excluding:
 *  - the abilities the minion ALREADY has ("this cannot be a keyword that
 *    we already have"),
 *  - Random Evolution itself (the grant is always another keyword),
 *  - Grandmaster and Second Chance (meta board-rule abilities — they are
 *    not keywords and their effects are board-state gated).
 *
 * Replacement rules are intentionally NOT applied to the pool (a grant is
 * direct, not an upgrade-path step — a directly granted Greater Heal works
 * with or without Heal; when the minion does have Heal, the grant's
 * abilitiesReplacedByUpgrade removal still travels with the addition, so
 * the one-form rules hold).
 */
export function randomEvolutionPool(
  currentAbilities: string[],
  minionLevel: number,
  allAbilityDefinitions: AbilityDefinitionSummary[]
): AbilityDefinitionSummary[] {
  return allAbilityDefinitions.filter(
    (def) =>
      def.id !== RANDOM_EVOLUTION_ABILITY_ID &&
      def.id !== GRANDMASTER_ABILITY_ID &&
      def.id !== SECOND_CHANCE_ABILITY_ID &&
      def.availableAtLevel <= minionLevel &&
      !currentAbilities.includes(def.id)
  );
}

/**
 * Resolves the keyword a Random Evolution upgrade grants: a uniform random
 * pick from randomEvolutionPool (rolled with the AUTHORITATIVE server RNG
 * — deterministic matches stay deterministic). Returns null when the pool
 * is empty (the option is never offered in that case — see
 * getEligibleAbilityUpgrades — so this is defensive).
 */
export function resolveRandomEvolutionGrant(
  currentAbilities: string[],
  minionLevel: number,
  allAbilityDefinitions: AbilityDefinitionSummary[],
  rng: EvolutionRng
): { id: string; name: string } | null {
  const pool = randomEvolutionPool(currentAbilities, minionLevel, allAbilityDefinitions);
  if (pool.length === 0) return null;
  const pick = pool[rng.nextInt(0, pool.length)];
  return { id: pick.id, name: pick.name };
}

/**
 * Validates that `abilityId` is a legal Random Evolution grant for a
 * minion in the given state — the shared check the server upgrade handler
 * uses to accept the client-confirmed keyword (the resolved keyword is NOT
 * in the minion's normal eligible list: the pool spans every class and the
 * grant bypasses class/replacement gates). The keyword must be in the
 * minion's evolution pool: level-eligible, not already owned, and not a
 * meta ability / Random Evolution itself.
 */
export function isValidRandomEvolutionGrant(
  abilityId: string,
  currentAbilities: string[],
  minionLevel: number,
  allAbilityDefinitions: AbilityDefinitionSummary[]
): boolean {
  if (abilityId === RANDOM_EVOLUTION_ABILITY_ID) return false;
  return randomEvolutionPool(currentAbilities, minionLevel, allAbilityDefinitions).some(
    (def) => def.id === abilityId
  );
}

/**
 * Resolves an ability ID to its human-readable name.
 * Returns the raw ID if the ability is not found.
 *
 * @param abilityId The ability ID to resolve.
 * @param allAbilityDefinitions All available ability definitions.
 * @returns The ability name, or the raw ID if not found.
 */
export function resolveAbilityName(
  abilityId: string,
  allAbilityDefinitions: AbilityDefinitionSummary[]
): string {
  const def = allAbilityDefinitions.find((d) => d.id === abilityId);
  return def ? def.name : abilityId;
}

/**
 * Resolves an array of ability IDs to their human-readable names.
 *
 * @param abilityIds Array of ability IDs to resolve.
 * @param allAbilityDefinitions All available ability definitions.
 * @returns Array of ability names (raw IDs used for unknown abilities).
 */
export function resolveAbilityNames(
  abilityIds: string[],
  allAbilityDefinitions: AbilityDefinitionSummary[]
): string[] {
  return abilityIds.map((id) => resolveAbilityName(id, allAbilityDefinitions));
}

/**
 * Formats a list of ability names for display.
 * - 0 abilities: ""
 * - 1 ability: "Lifesteal"
 * - 2 abilities: "Lifesteal, Killer"
 * - 3+ abilities: "Lifesteal, Killer, Explosive"
 *
 * @param names Array of ability names.
 * @returns Formatted display string.
 */
export function formatAbilityNames(names: string[]): string {
  if (names.length === 0) return '';
  return names.join(', ');
}

/**
 * Maps an upgradePathState to the array of branches available from that state.
 * For example, from "base" state a minion might offer ["hp_branch", "atk_branch"].
 * From "hp_branch" it might offer ["hp_branch_2", "atk_branch"] etc.
 */
export type UpgradeTree = Map<string, UpgradeBranch[]>;

/**
 * Full upgrade-path definition for a minion type.
 */
export interface UpgradePathDefinition {
  /** The baseMinionId this upgrade tree applies to. */
  baseMinionId: string;

  /** The upgrade tree mapping states to available branches. */
  branches: UpgradeTree;
}

/**
 * Validates and resolves upgrade paths.
 */
export class UpgradePathResolver {
  private paths: Map<string, UpgradePathDefinition> = new Map();

  /**
   * Registers an upgrade-path definition for a minion type.
   */
  public registerPath(pathDefinition: UpgradePathDefinition): void {
    this.paths.set(pathDefinition.baseMinionId, pathDefinition);
  }

  /**
   * Returns the available upgrade branches for a minion given its current upgradePathState.
   * @param baseMinionId The base minion ID.
   * @param currentPathState The minion's current upgradePathState.
   * @returns Array of available UpgradeBranch options, or empty array if none.
   * @throws Error if the baseMinionId has no registered upgrade path definition.
   */
  public getAvailableUpgrades(
    baseMinionId: string,
    currentPathState: string
  ): UpgradeBranch[] {
    const pathDef = this.paths.get(baseMinionId);
    if (!pathDef) {
      throw new Error(`No upgrade path definition registered for minion ${baseMinionId}`);
    }

    const branches = pathDef.branches.get(currentPathState);
    return branches ? [...branches] : [];
  }

  /**
   * Validates whether a specific upgrade branch ID is available for a minion's current state.
   * @param baseMinionId The base minion ID.
   * @param currentPathState The minion's current upgradePathState.
   * @param branchId The ID of the upgrade branch to validate.
   * @returns The UpgradeBranch if valid, or null if the branch is not available.
   */
  public validateUpgradeBranch(
    baseMinionId: string,
    currentPathState: string,
    branchId: string
  ): UpgradeBranch | null {
    const available = this.getAvailableUpgrades(baseMinionId, currentPathState);
    return available.find(branch => branch.id === branchId) ?? null;
  }

  /**
   * Checks if a minion has any further upgrades available from its current state.
   */
  public hasFurtherUpgrades(
    baseMinionId: string,
    currentPathState: string
  ): boolean {
    const available = this.getAvailableUpgrades(baseMinionId, currentPathState);
    return available.length > 0;
  }
}

/**
 * Creates a default upgrade tree for a minion type.
 * From every state (up to maxUpgrades depth), both +5 HP and +5 Attack branches are available.
 * @param baseMinionId The base minion ID.
 * @param maxUpgrades Maximum number of upgrades (default 5).
 * @returns An UpgradePathDefinition with the default tree.
 */
export function createDefaultUpgradeTree(
  baseMinionId: string,
  maxUpgrades: number = 5
): UpgradePathDefinition {
  const branches = new Map<string, UpgradeBranch[]>();

  // State naming: "base", then "u1_hp", "u1_atk", "u2_hp_hp", "u2_hp_atk", etc.
  // We generate states level by level.
  const statesByLevel: Map<number, string[]> = new Map();
  statesByLevel.set(0, ['base']);

  for (let level = 1; level <= maxUpgrades; level++) {
    const prevStates = statesByLevel.get(level - 1) ?? [];
    const currentStates: string[] = [];

    for (const prevState of prevStates) {
      const hpBranch: UpgradeBranch = {
        id: 'hp',
        name: 'Health Boost',
        hpChange: 5,
        attackChange: 0,
        nextPathState: `${prevState}_hp`,
      };
      const atkBranch: UpgradeBranch = {
        id: 'atk',
        name: 'Attack Boost',
        hpChange: 0,
        attackChange: 5,
        nextPathState: `${prevState}_atk`,
      };

      branches.set(prevState, [hpBranch, atkBranch]);
      currentStates.push(`${prevState}_hp`, `${prevState}_atk`);
    }

    statesByLevel.set(level, currentStates);
  }

  return { baseMinionId, branches };
}

/**
 * Validates an UpgradePathDefinition for structural correctness.
 */
export class UpgradePathValidator {
  /**
   * Validates an upgrade-path definition.
   * @throws Error if the definition is malformed.
   */
  static validate(pathDef: UpgradePathDefinition): void {
    if (!pathDef.baseMinionId) {
      throw new Error('Upgrade path definition missing baseMinionId');
    }
    if (!(pathDef.branches instanceof Map)) {
      throw new Error('Upgrade path branches must be a Map');
    }

    for (const [state, branches] of pathDef.branches) {
      if (!state) {
        throw new Error('Upgrade path has an empty state key');
      }
      if (!Array.isArray(branches)) {
        throw new Error(`Upgrade path branches for state "${state}" must be an array`);
      }
      for (const branch of branches) {
        this.validateBranch(branch, state);
      }
    }
  }

  private static validateBranch(branch: UpgradeBranch, state: string): void {
    if (!branch.id) {
      throw new Error(`Upgrade branch in state "${state}" missing id`);
    }
    if (!branch.name) {
      throw new Error(`Upgrade branch "${branch.id}" in state "${state}" missing name`);
    }
    if (typeof branch.hpChange !== 'number' || Number.isNaN(branch.hpChange)) {
      throw new Error(`Upgrade branch "${branch.id}" in state "${state}" missing hpChange`);
    }
    if (typeof branch.attackChange !== 'number' || Number.isNaN(branch.attackChange)) {
      throw new Error(`Upgrade branch "${branch.id}" in state "${state}" missing attackChange`);
    }
    if (!branch.nextPathState) {
      throw new Error(`Upgrade branch "${branch.id}" in state "${state}" missing nextPathState`);
    }
  }
}
